import { IS_FR, LocaleType } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { cva } from 'class-variance-authority';

interface UsePrescriptionStyleProps {
  locale: LocaleType;
  deviceType: EPlatform;
  paramValue?: 'sph' | 'cyl' | 'axis' | 'add' | 'prism' | 'pd' | 'od/os';
  waiMode?: boolean;
}

export const usePrescriptionStyle = ({
  locale,
  deviceType,
  paramValue,
  waiMode,
}: UsePrescriptionStyleProps) => {
  const isWeb = deviceType === EPlatform.WEB;
  const isShowPrismForm = paramValue === 'prism';
  const grayColor = waiMode ? '' : 'text-secondary-text';

  // 处方表单容器样式
  const prescriptionFormStyle = cva('prescription-form', {
    variants: {
      locale: {
        en: 'w-[620px]',
        fr: 'w-[720px]',
        de: 'w-[620px]',
        es: 'w-[620px]',
        it: 'w-[620px]',
        pt: 'w-[620px]',
        fi: 'w-[620px]',
        nl: 'w-[620px]',
      },
    },
  });

  // 表单项宽度样式
  const formItemWidthStyle = cva('prescription-form box-border', {
    variants: {
      locale: {
        en: 'w-[124px]',
        fr: 'w-[148px]',
        de: 'w-[124px]',
        es: 'w-[124px]',
        it: 'w-[124px]',
        pt: 'w-[124px]',
        fi: 'w-[124px]',
        nl: 'w-[124px]',
      },
    },
  });

  // Prism 表单项宽度样式
  const prismFormItemWidthStyle = cva('box-border', {
    variants: {
      locale: {
        en: 'w-[124px]',
        fr: 'w-[148px]',
        de: 'w-[124px]',
        es: 'w-[124px]',
        it: 'w-[124px]',
        pt: 'w-[124px]',
        fi: 'w-[124px]',
        nl: 'w-[124px]',
      },
    },
  });

  // 基础表单项样式
  const baseFormItemStyle = `title ${formItemWidthStyle({ locale })} h-48 border-r flex items-center justify-center font-bb text-14 text-[#282828] border-[#d4d4d4]`;

  // 获取悬浮框的类名
  const getCoverBlockClassName = () => {
    const baseClasses =
      'absolute bg-[rgba(70,58,232,0.1)] border border-[#463ae8] z-50 param-cover';

    if (!isWeb) {
      // 移动端样式
      const mobileClassMap = {
        sph: 'w-1/5 left-1/5 h-[146px] top-0',
        cyl: 'w-1/5 left-2/5 h-[146px] top-0',
        axis: 'w-1/5 left-3/5 h-[146px] top-0',
        add: 'w-1/5  h-[146px] top-0 left-4/5 rounded-r-6 translate-x-[-1px]',
        pd: 'w-2/5 h-[48px] rounded-6 top-[156px] left-0  translate-y-[0]',
        prism: 'w-3/4 left-1/4 rounded-r-6 top-0 h-[146px]',
      };
      return `${baseClasses} ${mobileClassMap[paramValue] || 'w-1/4 h-[152px] left-1/4'}`;
    } else {
      // PC端样式
      const widthClass = IS_FR ? 'w-[148px]' : 'w-[124px]';
      const heightClass = 'h-[146px]';

      const positionClassMap = {
        sph: 'left-[128px] top-0',
        cyl: IS_FR ? 'left-[276px] top-0' : 'left-[250px] top-0',
        axis: IS_FR ? 'left-[424px] top-0' : 'left-[372px] top-0',
        add: IS_FR ? 'left-[572px] top-0 rounded-r-6' : 'left-[495px] top-0 rounded-r-6',
        pd: `left-0 top-[160px] rounded-6 ${IS_FR ? 'w-[276px]' : 'w-[252px]'} h-[48px]`,
        prism: `rounded-r-6 left-[127px] top-0 ${IS_FR ? 'w-[592px]' : 'w-[492px]'} h-[146px]`,
      };

      const positionClass = positionClassMap[paramValue] || 'left-[136px] top-0';

      // 对于特殊情况，直接返回完整的类名
      if (['pd', 'prism', 'od/os'].includes(paramValue)) {
        return `${baseClasses} ${positionClass}`;
      }

      return `${baseClasses} ${widthClass} ${heightClass} ${positionClass}`;
    }
  };

  return {
    prescriptionFormStyle,
    formItemWidthStyle,
    prismFormItemWidthStyle,
    baseFormItemStyle,
    getCoverBlockClassName,
    grayColor,
    isShowPrismForm,
    isWeb,
  };
};
